import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {convertDT, dateToDatetime} from '../helpers';
import {FeatureService} from '../../features/dashboard/feature.service';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {take} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {CalendarDateFormatter, CalendarEventTitleFormatter} from 'angular-calendar';
import {CustomEventTitleFormatterProvider} from '../providers/custom-event-title-formatter.provider';
import {CustomDateFormatterProvider} from '../providers/custom-date-formatter.provider';
import {getUserFirstName} from '../../core/store/account/selectors';

@Component({
  selector: 'app-modal-calendar',
  providers: [{
    provide: CalendarEventTitleFormatter,
    useClass: CustomEventTitleFormatterProvider
  }, {
    provide: CalendarDateFormatter,
    useClass: CustomDateFormatterProvider
  }],
  template: `
    <div class="d-flex align-items-center mb-5" *ngIf="(operatori$ | async).length > 1">
      Filtra per operatore
      <select class="form-control w-auto ml-3"
              [ngModel]="operatoreId"
              (ngModelChange)="handleOperatoreChange($event)">
        <option *ngFor="let operatore of operatori$ | async" [ngValue]="operatore.id">
          {{ operatore.nome }}
        </option>
      </select>
    </div>
    <mwl-calendar-day-view
      [viewDate]="date"
      [events]="this.events$ | async"
      [dayStartHour]="8"
      [dayEndHour]="20"
      [hourSegmentHeight]="80"
      [hourSegments]="1"
      [tooltipAppendToBody]="false"
      (hourSegmentClicked)="onSegmentClick.emit($event)"
    ></mwl-calendar-day-view>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ModalCalendarComponent implements OnInit, OnChanges {

  @Input() date: Date = null;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onSegmentClick: EventEmitter<{
    date: Date;
    sourceEvent: MouseEvent;
  }> = new EventEmitter();
  events$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  // Il primo elemento e' l'operatore principale, che sugli eventi vale operatore_id null.
  operatori$: BehaviorSubject<{ id: number | null, nome: string }[]> = new BehaviorSubject([]);
  operatoreId: number | null = null;
  private eventi: any[] = [];

  constructor(private service: FeatureService, private store: Store) {
  }

  ngOnInit() {

    combineLatest([
      this.store.select(getUserFirstName).pipe(take(1)),
      this.service.getSottoOperatori()
    ]).subscribe(([nomeUtente, res]: [string, any]) => {

      const sottoOperatori = (res && res.data ? res.data : [])
        .map(operatore => ({id: +operatore.id, nome: `${operatore.cognome} ${operatore.nome}`}));

      this.operatori$.next(sottoOperatori.length
        ? [{id: null, nome: nomeUtente || 'Operatore principale'}, ...sottoOperatori]
        : []);
      this.pubblicaEventi();

    });

  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.date && changes.date.currentValue && changes.date.currentValue !== changes.date.previousValue) {
      this.getEvents();
    }

  }

  getEvents() {

    this.service.calendar({ date: dateToDatetime(this.date, false), type: 'day' }).subscribe((events) => {
      this.eventi = events.map(item => ({
        id: item.id,
        draggable: false,
        allDay: item.type === 'chiusura',
        title: `${item.description}`,
        start: new Date(convertDT(item.data)),
        end: item.type === 'chiusura' ? null : new Date(convertDT(item.dataFine)),
        cssClass: `event-${item.type} event-stato-${item.stato}`,
        meta: {
          cliente: item.cliente,
          descrizione: item.description,
          note: item.note,
          stato: item.stato,
          durata: item.durata,
          tipo: item.type,
          operatoreId: item.operatore_id != null ? +item.operatore_id : null,
        }
      }));
      this.pubblicaEventi();
    });

  }

  handleOperatoreChange(operatoreId: number | null) {

    this.operatoreId = operatoreId != null ? +operatoreId : null;
    this.pubblicaEventi();

  }

  private pubblicaEventi() {

    // Senza sotto-operatori la tendina non c'e' e non si filtra niente.
    if (this.operatori$.value.length < 2) {
      this.events$.next(this.eventi);
      return;
    }

    // Le chiusure non appartengono a nessun operatore e restano sempre visibili.
    this.events$.next(this.eventi.filter(
      evento => evento.meta.tipo === 'chiusura' || evento.meta.operatoreId === this.operatoreId
    ));

  }

}
